//#region types
import { CurrencyEnums, FormEnums } from "models/enums";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import {
  FormFieldDefinitionType,
  NumberFormatFieldExtraProps,
  SelectionExtraProps,
  AutoCompleteExtraProps,
  CurrencyFieldExtraProps,
  EditableTableFieldProps,
  MonthRangeFormFieldProps,
} from "models/propTypes/genericFormProps";
import { TimeReportingProjectMemberFormViewProps } from "models/propTypes/timeReportingProjectPropTypes";
import { TranslationKeys } from "translations";
import { CommonConstants, FormFieldConstants } from "utils/constants";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { FormikProps } from "formik";
import { WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
import cssExports from "theme/styles/main.module.scss";
//#endregion

function useMemberSelfBillingFields(
  props: TimeReportingProjectMemberFormViewProps
): FormFieldDefinitionType<TimeReportingProjectFormTypes.ProjectMember> {
  const { project } = props;

  const getPaymentTermsItems = (): AutocompleteListItem[] => [
    {
      id: 10,
      value: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.TimeReportingProject.SelfBillingPaymentTerms10Days
      ),
    },
    {
      id: 14,
      value: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.TimeReportingProject.SelfBillingPaymentTerms14Days
      ),
    },
    {
      id: 30,
      value: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.TimeReportingProject.SelfBillingPaymentTerms30Days
      ),
    },
    {
      id: -1,
      value: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.TimeReportingProject.SelfBillingPaymentTermsWhenEndCustomerPaid
      ),
    },
  ];

  return {
    selfBillingEnabled: {
      label: TranslationKeys.TimeReportingProject.SelfBillingToSubcontractor,
      descriptionText: TranslationKeys.TimeReportingProject.SelfBillingToSubcontractorDescription,
      type: FormEnums.FormFieldType.TOGGLE,
    },
    selfBillingOrgNumber: {
      label: TranslationKeys.Invoice.OrganizationNumber,
      descriptionText: TranslationKeys.TimeReportingProject.SelfBillingOrgNumberDescription,
      type: FormEnums.FormFieldType.NUMBER,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) => !values.selfBillingEnabled,
      extraProps: {
        allowLeadingZeros: true,
        format: CommonConstants.OrganizationNumberMask,
        placeholder: TranslationKeys.Invoice.OrganizationNumber,
      } as NumberFormatFieldExtraProps,
    },
    selfBillingEmail: {
      label: TranslationKeys.Common.Email,
      descriptionText: TranslationKeys.TimeReportingProject.SelfBillingEmailDescription,
      type: FormEnums.FormFieldType.TEXT,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) => !values.selfBillingEnabled,
      placeholder: TranslationKeys.Common.Email,
    },
    selfBillingPaymentMethod: {
      label: TranslationKeys.TimeReportingProject.SelfBillingPaymentMethod,
      type: FormEnums.FormFieldType.CHOICE,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) => !values.selfBillingEnabled,
      extraProps: {
        items: [
          {
            id: ApiModel.SelfBillingPaymentMethod.Bankgiro,
            value: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.TimeReportingProject.SelfBillingPaymentMethodBankgiro
            ),
          },
          {
            id: ApiModel.SelfBillingPaymentMethod.Plusgiro,
            value: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.TimeReportingProject.SelfBillingPaymentMethodPlusgiro
            ),
          },
          {
            id: ApiModel.SelfBillingPaymentMethod.IBAN,
            value: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.TimeReportingProject.SelfBillingPaymentMethodIBAN
            ),
          },
          {
            id: ApiModel.SelfBillingPaymentMethod.BankTransfer,
            value: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.TimeReportingProject.SelfBillingPaymentMethodBankTransfer
            ),
          },
        ],
        referenceFieldName: FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingPaymentMethod,
      } as SelectionExtraProps<TimeReportingProjectFormTypes.ProjectMember>,
    },
    selfBillingBankgiro: {
      label: TranslationKeys.TimeReportingProject.SelfBillingBankgiro,
      type: FormEnums.FormFieldType.TEXT,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) =>
        !values.selfBillingEnabled ||
        values.selfBillingPaymentMethod !== ApiModel.SelfBillingPaymentMethod.Bankgiro,
      placeholder: TranslationKeys.TimeReportingProject.SelfBillingBankgiro,
    },
    selfBillingPlusgiro: {
      label: TranslationKeys.TimeReportingProject.SelfBillingPlusgiro,
      type: FormEnums.FormFieldType.TEXT,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) =>
        !values.selfBillingEnabled ||
        values.selfBillingPaymentMethod !== ApiModel.SelfBillingPaymentMethod.Plusgiro,
      placeholder: TranslationKeys.TimeReportingProject.SelfBillingPlusgiro,
    },
    selfBillingClearingNumber: {
      label: TranslationKeys.Settings.ClearingNumber,
      descriptionText: TranslationKeys.Settings.ClearingNumberDescription,
      type: FormEnums.FormFieldType.TEXT,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) =>
        !values.selfBillingEnabled ||
        values.selfBillingPaymentMethod !== ApiModel.SelfBillingPaymentMethod.BankTransfer,
      placeholder: TranslationKeys.Settings.ClearingNumber,
    },
    selfBillingAccountNumber: {
      label: TranslationKeys.Settings.AccountNumber,
      descriptionText: TranslationKeys.Settings.AccountNumberDescription,
      type: FormEnums.FormFieldType.TEXT,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) =>
        !values.selfBillingEnabled ||
        values.selfBillingPaymentMethod !== ApiModel.SelfBillingPaymentMethod.BankTransfer,
      placeholder: TranslationKeys.Settings.AccountNumber,
    },
    selfBillingIBAN: {
      label: TranslationKeys.IncomingInvoice.IBAN,
      type: FormEnums.FormFieldType.TEXT,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) =>
        !values.selfBillingEnabled ||
        values.selfBillingPaymentMethod !== ApiModel.SelfBillingPaymentMethod.IBAN,
      placeholder: TranslationKeys.IncomingInvoice.IBAN,
    },
    selfBillingPaymentTerms: {
      label: TranslationKeys.Common.PaymentTerms,
      type: FormEnums.FormFieldType.AUTOCOMPLETE,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) => !values.selfBillingEnabled,
      extraProps: {
        freeSolo: false,
        items: getPaymentTermsItems(),
        referenceFieldName: FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingPaymentTerms,
        formatInputValue: (value?: string) => {
          // Convert stored ID (number as string) to display value (string)
          if (!value) return "";
          const id = typeof value === "string" ? parseInt(value, 10) : value;
          const items = getPaymentTermsItems();
          const item = items.find((i) => i.id === id);
          return item?.value ?? "";
        },
        onItemSelect: (
          item: AutocompleteListItem,
          name: string,
          formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectMember>
        ) => {
          // Store the ID (number) instead of the value (string) in the main field
          formikProps.setFieldValue(name, item.id);
        },
      } as AutoCompleteExtraProps<TimeReportingProjectFormTypes.ProjectMember>,
    },
    selfBillingCalculationMethod: {
      label: TranslationKeys.TimeReportingProject.SelfBillingCalculationMethod,
      descriptionText: TranslationKeys.TimeReportingProject.SelfBillingCalculationMethodDescription,
      type: FormEnums.FormFieldType.CHOICE,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) => !values.selfBillingEnabled,
      extraProps: {
        items: [
          {
            id: ApiModel.SelfBillingCalculationMethod.Percentage,
            value: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.TimeReportingProject.SelfBillingCalculationMethodPercent
            ),
          },
          {
            id: ApiModel.SelfBillingCalculationMethod.HourlyRate,
            value: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.TimeReportingProject.SelfBillingCalculationMethodHourlyRate
            ),
          },
          {
            id: ApiModel.SelfBillingCalculationMethod.FixedPrice,
            value: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.TimeReportingProject.SelfBillingCalculationMethodFixedPrice
            ),
          },
        ],
        referenceFieldName: FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingCalculationMethod,
      } as SelectionExtraProps<TimeReportingProjectFormTypes.ProjectMember>,
    },
    selfBillingPercentage: {
      label: TranslationKeys.TimeReportingProject.SelfBillingPercentage,
      descriptionText: TranslationKeys.TimeReportingProject.SelfBillingPercentageDescription,
      type: FormEnums.FormFieldType.NUMBER,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) =>
        !values.selfBillingEnabled ||
        values.selfBillingCalculationMethod !== ApiModel.SelfBillingCalculationMethod.Percentage,
      extraProps: {
        suffix: "%",
        placeholder: TranslationKeys.TimeReportingProject.SelfBillingPercentage,
      } as NumberFormatFieldExtraProps,
    },
    selfBillingHourlyRates: {
      label: TranslationKeys.TimeReportingProject.SelfBillingHourlyRate,
      descriptionText: TranslationKeys.TimeReportingProject.SelfBillingHourlyRateDescription,
      type: FormEnums.FormFieldType.EDITABLETABLE,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) =>
        !values.selfBillingEnabled ||
        values.selfBillingCalculationMethod !== ApiModel.SelfBillingCalculationMethod.HourlyRate,
      innerFields: {
        categoryName: {
          label: "",
          type: FormEnums.FormFieldType.TEXT,
          readonly: true,
          classes: {
            formControl: cssExports.selfBillingCategoryCell,
          },
        },
        amount: {
          label: "",
          type: FormEnums.FormFieldType.CURRENCY,
          placeholder: TranslationKeys.Common.Amount,
          classes: {
            input: cssExports.selfBillingAmountInput,
          },
          onChangeHandler: props.onSelfBillingAmountChange,
          extraProps: {
            currency: project?.currency ?? CurrencyEnums.CurrencyCode.SEK,
          } as CurrencyFieldExtraProps<TimeReportingProjectFormTypes.ProjectMember>,
        },
      },
      extraProps: {
        isCompactTable: true,
        hideActions: true,
        showColumnHeaders: true,
        idFieldKey: "categoryName" as keyof TimeReportingProjectFormTypes.ProjectMember,
      } as EditableTableFieldProps<TimeReportingProjectFormTypes.ProjectMember>,
    },
    selfBillingChangesApplyFrom: {
      label: TranslationKeys.TimeReportingProject.ChangesApplyFromTheFirstInTheMonth,
      controlLabel: " ",
      type: FormEnums.FormFieldType.MONTH,
      extraProps: {
        hideClearable: true,
        popperPositionFixed: true,
        // A month further back than the one running would rewrite a rate that has already been
        // invoiced, and never shows up under "Köade förändringar" for the user to undo.
        minDate: DateHelper.getFirstDayOfMonth(new Date()),
      } as MonthRangeFormFieldProps,
      onChangeHandler: props.onSelfBillingChangesApplyMonthChange,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) =>
        !props.hasSelfBillingRateHistory ||
        !values.selfBillingEnabled ||
        values.selfBillingCalculationMethod !== ApiModel.SelfBillingCalculationMethod.HourlyRate,
    },
    selfBillingFutureRates: {
      label: TranslationKeys.TimeReportingProject.SelfBillingQueuedRateChanges,
      type: FormEnums.FormFieldType.EDITABLETABLE,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) =>
        !props.hasSelfBillingRateHistory ||
        !values.selfBillingEnabled ||
        values.selfBillingCalculationMethod !== ApiModel.SelfBillingCalculationMethod.HourlyRate ||
        !values.selfBillingFutureRates?.length,
      innerFields: {
        categoryName: {
          label: "",
          type: FormEnums.FormFieldType.TEXT,
          readonly: true,
          classes: {
            formControl: cssExports.selfBillingCategoryCell,
          },
        },
        amount: {
          label: "",
          type: FormEnums.FormFieldType.CURRENCY,
          readonly: true,
          classes: {
            input: cssExports.selfBillingAmountInput,
          },
          extraProps: {
            currency: project?.currency ?? CurrencyEnums.CurrencyCode.SEK,
          } as CurrencyFieldExtraProps<TimeReportingProjectFormTypes.ProjectMember>,
        },
        fromDate: {
          label: " ",
          type: FormEnums.FormFieldType.MONTH,
          readonly: true,
          extraProps: {
            popperPositionFixed: true,
            hideClearable: true,
          },
        },
      },
      extraProps: {
        isCompactTable: true,
        hideActions: true,
        showColumnHeaders: true,
        // Two months queued for the same category share a category name, so the row key carries
        // the month as well — see selfBillingRowKey in the member container.
        idFieldKey: "rowKey" as keyof TimeReportingProjectFormTypes.ProjectMember,
      } as EditableTableFieldProps<TimeReportingProjectFormTypes.ProjectMember>,
    },
    selfBillingMonthlyCost: {
      label: TranslationKeys.TimeReportingProject.SelfBillingMonthlyCost,
      descriptionText: TranslationKeys.TimeReportingProject.SelfBillingMonthlyCostDescription,
      type: FormEnums.FormFieldType.CURRENCY,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) =>
        !values.selfBillingEnabled ||
        values.selfBillingCalculationMethod !== ApiModel.SelfBillingCalculationMethod.FixedPrice,
      extraProps: {
        currency: project?.currency ?? CurrencyEnums.CurrencyCode.SEK,
        placeholder: TranslationKeys.TimeReportingProject.SelfBillingMonthlyCost,
      } as CurrencyFieldExtraProps<TimeReportingProjectFormTypes.ProjectMember>,
    },
  };
}

export default useMemberSelfBillingFields;
